// Small formatters of the import screen (dates, periods, amounts). Pure functions, tested in format.test.ts.
import { formatEur, parseUserAmount, toCents } from '../../api/money';

/** "2026-09-30" → "30.09.2026" */
export const dmy = (iso?: string | null) => (iso ? iso.slice(8, 10) + '.' + iso.slice(5, 7) + '.' + iso.slice(0, 4) : '');
/** "30.09.2026" · "30-09-26" · "30/9/2026" → "2026-09-30"; undefined for text that is not a real date. */
export const isoOf = (txt: string) => {
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(txt.trim());
  if (!m) return undefined;
  const iso = (m[3].length === 2 ? '20' + m[3] : m[3]) + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0');
  const d = new Date(iso + 'T00:00:00Z');
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso ? iso : undefined;
};
/** "09/2026" · "9.2026" → "2026-09" */
export const perOf = (txt: string) => { const m = /^(\d{1,2})[./-](\d{4})$/.exec(txt.trim()); return m && Number(m[1]) >= 1 && Number(m[1]) <= 12 ? m[2] + '-' + m[1].padStart(2, '0') : undefined; };
/** "2026-09" → "09/2026" */
export const perTxt = (p?: string) => (p ? p.slice(5, 7) + '/' + p.slice(0, 4) : '');
/** API decimal string → "1.097,86" (field display, without €). */
export const money = (d?: string | null) => (d == null || d === '' ? '' : formatEur(toCents(d)).replace(' €', ''));
export const addDays = (iso: string, n: number) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
/** Today as a Luxembourg calendar day (escompte deadlines, current month), also on a device set to another time zone. */
export const todayIso = (now = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Luxembourg', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000);
export const initials = (s?: string) => (s || '').split(/[\s.·-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '—';
/** "17 %" · "5,5" → "17" · "5.5" */
export const cleanRate = (t: string) => t.replace('%', '').replace(',', '.').trim();

// Typed text of a field is kept as typed until it is valid (user report 07.10.: a half-typed HTVA vanished on blur).
// These say whether a field's raw text can be saved; empty is valid (it clears the field on purpose).
export const amountOk = (t: string) => !t.trim() || parseUserAmount(t) !== undefined;
export const dateOk = (t: string) => !t.trim() || !!isoOf(t);
export const periodOk = (t: string) => !t.trim() || !!perOf(t);
/** VAT/escompte rate: 0–99 with up to two decimals ("17", "5,5 %", "2.25"). */
export const rateOk = (t: string) => !t.trim() || /^\d{1,2}(\.\d{1,2})?$/.test(cleanRate(t));
/** On leaving a field: save the text, or keep it (marked) because it is not valid yet. */
export const onLeave = (raw: string, ok?: (t: string) => boolean): 'save' | 'keep' => (ok && !ok(raw) ? 'keep' : 'save');

// ── „Einsetzen in ›“ (wave 1, 26.10.2026): text marked in the receipt preview → a field value. Each picks the first value of
// its kind in the marked snippet („Montant total 1.234,50 EUR“ → 1234.50); undefined when there is none (field stays). ──
/** Amount → API decimal ("1234.50"); accepts 1.234,50 · 1 234,50 · 1,234.50 · 5,38 € · -12,00. */
export const pickAmount = (txt: string) => {
  // Dates and percentages are no amounts („Echéance 13.11.2026“, „TVA 17 % 179,37“).
  txt = txt.replace(/\d{4}-\d{2}-\d{2}|\b\d{1,2}[./-]\d{1,2}[./-](?:\d{4}|\d{2})\b/g, ' ').replace(/\d+(?:[.,]\d+)?\s*%/g, ' ');
  const all = [...txt.replace(/\s+/g, ' ').matchAll(/-?\d{1,3}(?:,\d{3})+\.\d{1,2}|-?\d{1,3}(?:[ .'\u2019\u00a0\u202f]\d{3})+(?:[.,]\d{1,2})?|-?\d+(?:[.,]\d{1,2})?/g)].map((m) => m[0]);
  // An amount with cents wins over a bare count („Ciment 25 kg … 1.234,50“).
  const m = all.find((x) => /[.,]\d{2}$/.test(x)) ?? all[0];
  if (!m) return undefined;
  let s = m.replace(/[ '\u2019\u00a0\u202f]/g, '');
  // 1,234.50 (English): comma as thousands separator.
  if (/^-?\d{1,3}(,\d{3})+\.\d{1,2}$/.test(s)) s = s.replace(/,/g, '');
  return parseUserAmount(s);
};
/** Date → ISO; accepts 12.10.2026 · 12/10/26 · 12-10-2026 · 2026-10-12, also inside a longer snippet. */
export const pickDate = (txt: string) => {
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(txt);
  if (iso) return isoOf(iso[3] + '.' + iso[2] + '.' + iso[1]);
  const m = /\b\d{1,2}[./-]\d{1,2}[./-](?:\d{4}|\d{2})\b/.exec(txt);
  return m ? isoOf(m[0]) : undefined;
};
/** Month → "YYYY-MM"; accepts 09/2026 · 9.2026 · a full date (its month). */
export const pickPeriod = (txt: string) => { const d = pickDate(txt); if (d) return d.slice(0, 7); const m = /\b\d{1,2}[./-]\d{4}\b/.exec(txt); return m ? perOf(m[0]) : undefined; };
/** Card: the last 4 digits of a card number („•••• 4821“, „XXXX XXXX XXXX 4821“). */
export const pickCard4 = (txt: string) => { const m = /(\d{4})\D*$/.exec(txt.trim()); return m ? m[1] : undefined; };
/** Text fields (party, number, communication): one line, trimmed, without surrounding punctuation. */
export const pickText = (txt: string, max = 120) => { const t = txt.replace(/\s+/g, ' ').trim().replace(/^[\s:;,.–-]+|[\s:;,–-]+$/g, ''); return t ? t.slice(0, max) : undefined; };
